import { type ReactNode } from 'react';

import { Stack } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import {
  type Flow,
  type FlowAlt,
  type HandoverSpec,
} from '@/designs-dev/handover/types';
import IntakeFlow from '@/karehero-app/tell-us-once/intake/Flow';
import ActionsScreen from '@/karehero-app/actions/ActionsScreen';
import AppTab from '@/karehero-app/shell/AppTab';
import HelpScreen from '@/karehero-app/help/HelpScreen';
import FundingScreen from '@/karehero-app/access/FundingScreen';
import { preSignUpSpec } from '@/karehero-app/handover/pre-sign-up';
import { signUpSpec, verificationIntro } from '@/karehero-app/handover/sign-up';
import { intakeProfileReviewSpec } from '@/karehero-app/handover/intake-profile-review';
import { actionsSpec } from '@/karehero-app/handover/actions';
import { benefitsCalculatorFlowsSpec } from '@/karehero-app/handover/benefits-calculator';
import { attendanceAllowanceFlowsSpec } from '@/karehero-app/handover/attendance-allowance';
import CareProfileScreen from '@/karehero-app/tell-us-once/intake/CareProfileScreen';
import {
  CARER_INITIALS,
  PROFILE_TABS,
  RECIPIENT,
} from '@/karehero-app/tell-us-once/intake/content';

const noop = () => {};

// The Masters pages: one per place a journey starts, each composed from the
// projects that own the screens.
//
// Composed, not rewritten. Every flow here is the same object the project that
// owns it renders, so a screen that changes changes on both pages and there is
// nothing to keep in step. This file decides two things: the order, and which
// journey each flow reads under.
const under = (journey: string, flows: Flow[]): Flow[] =>
  // Parked keeps its own tag. It reads last and collapsed wherever it appears,
  // and folding it into the journey it was lifted out of would put work nobody
  // is doing in the middle of work somebody is.
  flows.map((flow) =>
    flow.journey === 'Parked' ? flow : { ...flow, journey },
  );

/** Same row, without its readme. Dropped here only; the source page keeps it. */
const noReadme = (title: string, flows: Flow[]): Flow[] =>
  flows.map((flow) =>
    flow.title === title
      ? { ...flow, note: undefined, noteAfter: undefined }
      : flow,
  );

/** Same row, with a readme beside it. Added here only, the same way. */
const withReadme = (title: string, note: ReactNode, flows: Flow[]): Flow[] =>
  flows.map((flow) => (flow.title === title ? { ...flow, note } : flow));

/** Same row, with something else drawn under one of its steps. */
const withAlt = (
  title: string,
  stepId: string,
  alt: FlowAlt,
  flows: Flow[],
): Flow[] =>
  flows.map((flow) =>
    flow.title !== title
      ? flow
      : {
          ...flow,
          steps: flow.steps.map((step) =>
            step.id === stepId
              ? { ...step, alt: [...[step.alt ?? []].flat(), alt] }
              : step,
          ),
        },
  );

// Tapping a fact opens it for editing in place. The Tell Us Once page carries
// this as one of four states, one per reason a fact needs changing: something we
// read wrong, a gap, two sources disagreeing, something gone stale. Here it is
// the one thing the row does not otherwise say, which is that the facts are not
// read-only.
const editAFact: FlowAlt = {
  title: 'Tapping one to change it',
  render: () => <IntakeFlow initial='review-facts-read' option={2} />,
};

// Two notes, one column. The verification rules were a flow of their own on the
// sign-up page, with no steps in it: on this page a row that draws nothing sits
// between two that do, so the rule reads under the row it is about instead.
// Sunflower keeps it from reading as more of the same note.
//
// The rule only, not the three screens it resolves to. Those screens are in the
// row beside it here, which they are not on the sign-up page.
const signUpReadme = (
  <Stack spacing={2}>
    <Note title='Readme' variant='body2'>
      <Stack spacing={1.5}>
        <span>
          The order sign-up happens in is not settled. There are technical
          constraints we have not fully found yet.
        </span>
        <span>
          One of them is certain. The create account screens are Kinde's, so how
          much of them we can design is limited. They are recreated here as they
          should appear, but we cannot put our own patterns on them, buttons
          included.
        </span>
        <span>
          After that it is product work. We still need requirements written for
          every user journey that can happen.
        </span>
      </Stack>
    </Note>
    <Note title='Verification note' colour='sunflower' variant='body2'>
      {verificationIntro}
    </Note>
  </Stack>
);

/**
 * The avatar's sheet, open on the page.
 *
 * It is the only control in the app bar that is about the person using the app
 * rather than the person they care for, and it is behind a tap, so a page that
 * only drew the screens showed an avatar and left what it does to whoever
 * thought to press it. Each page carries it open as a row of its own.
 *
 * Two sheets, not one. Before there is an account worth managing it is Help and
 * a way out; in the app their own details and their circle sit above those,
 * because by now there is something in the account to change.
 */
const settingsFlow = (journey: string, steps: Flow['steps']): Flow => ({
  title: 'Settings, from the avatar',
  journey,
  steps,
});

/** The Actions tab as a tab, the way its own page draws it. */
const actionsTab = (props: Parameters<typeof ActionsScreen>[0] = {}) => (
  <AppTab active='actions'>
    <ActionsScreen
      title={RECIPIENT}
      avatarInitials={CARER_INITIALS}
      {...props}
    />
  </AppTab>
);

/**
 * Three pages, not one.
 *
 * It was a single Onboarding page running getting in, the account and the
 * questions together, which is the journey as somebody walks it but not as
 * anybody works on it: the three parts belong to different projects, move at
 * different speeds and are read by different people. Splitting them means a
 * reader can be handed the one that is theirs.
 *
 * Composed, not rewritten. Every flow here is the same object the project that
 * owns it renders, so a screen that changes changes in both places.
 */
export const masterPreRegistrationSpec: HandoverSpec = {
  id: 'master-pre-registration',
  title: 'Pre-registration',
  description: 'Everything before the app asks for anything.',
  links: [
    { label: 'Getting in: pre-sign-up', url: '/onboarding/flows/pre-sign-up' },
  ],
  flows: under(
    'Getting in',
    noReadme('Option one: chat style intro', preSignUpSpec.flows),
  ),
  states: [],
  components: {},
};

export const masterRegistrationSpec: HandoverSpec = {
  id: 'master-registration',
  title: 'Registration',
  description: 'Making the account.',
  links: [{ label: 'Getting in: sign up', url: '/onboarding/flows/sign-up' }],
  flows: [
    ...under(
      'Sign up',
      withReadme(
        'Sign up to home',
        signUpReadme,
        // Its readme is up there now, and it had nothing else in it.
        signUpSpec.flows.filter((flow) => flow.title !== 'Verification'),
      ),
    ),
    settingsFlow('Sign up', [
      {
        id: 'settings-closed',
        title: 'Any step with the bar',
        tap: 'The avatar',
        render: () => (
          <FundingScreen
            onContinue={noop}
            onSignOut={noop}
            initialChoice='work'
          />
        ),
      },
      {
        id: 'settings-open',
        title: 'Help, and a way out',
        render: () => (
          <FundingScreen
            onContinue={noop}
            onSignOut={noop}
            initialChoice='work'
            initialSheetOpen
          />
        ),
      },
    ]),
  ],
  states: [],
  components: {},
};

export const masterOnboardingSpec: HandoverSpec = {
  id: 'master-onboarding',
  title: 'Onboarding',
  description: 'The questions after the account, and the review that follows.',
  links: [
    {
      label: 'Tell Us Once: onboarding',
      url: '/tell-us-once/flows/intake-profile-review',
    },
  ],
  flows: [
    ...under(
      'Tell Us Once',
      withAlt(
        'Review',
        'review-facts',
        editAFact,
        intakeProfileReviewSpec.flows,
      ),
    ),
    settingsFlow('Tell Us Once', [
      {
        id: 'settings-closed',
        title: 'Any question',
        tap: 'The avatar',
        render: () => <IntakeFlow initial='chat-v2-situation' option={2} />,
      },
      {
        id: 'settings-open',
        title: 'Help, and a way out',
        render: () => (
          <IntakeFlow initial='chat-v2-situation' option={2} sheetOpen />
        ),
      },
    ]),
  ],
  states: [],
  components: {},
};

/**
 * Actions, as its own page.
 *
 * One journey rather than several: everything on this tab is the same object
 * being made, read, changed or finished, and splitting it would separate a list
 * from the thing the list holds.
 */
export const masterActionsSpec: HandoverSpec = {
  id: 'master-actions',
  title: 'Actions',
  description: 'The tab, and everything you can do to an action from it.',
  links: [
    {
      label: 'Actions: concepting',
      url: '/meaningful-interactions/flows/actions',
    },
  ],
  flows: [
    ...under('Actions', actionsSpec.flows),
    settingsFlow('Actions', [
      {
        id: 'settings-open',
        title: 'Settings',
        tap: 'The avatar',
        render: () => actionsTab({ account: { initialSheetOpen: true } }),
      },
      {
        id: 'settings-details',
        title: 'Your details',
        tap: '‘Your details’',
        render: () =>
          actionsTab({
            account: { initialSheetOpen: true, initialView: 'details' },
          }),
      },
    ]),
  ],
  states: [],
  components: {},
};

/**
 * Settings, as its own page.
 *
 * It was the last row on Profile, which is where the avatar happens to sit
 * rather than where settings belong: everything else on that page is about the
 * person being cared for, and this is the only thing about the person using the
 * app. A row at the bottom of somebody else's record is not a place to look for
 * your own account.
 */
export const masterSettingsSpec: HandoverSpec = {
  id: 'master-settings',
  title: 'Settings',
  description: 'The account, from the avatar: your details, and your circle.',
  links: [],
  flows: [
    settingsFlow('Settings', [
      {
        id: 'settings-open',
        title: 'Settings',
        tap: 'The avatar',
        render: () => (
          <CareProfileScreen
            account={{ initials: CARER_INITIALS, initialSheetOpen: true }}
          />
        ),
      },
      {
        id: 'settings-details',
        title: 'Your details',
        tap: '‘Your details’',
        render: () => (
          <CareProfileScreen
            account={{
              initials: CARER_INITIALS,
              initialSheetOpen: true,
              initialView: 'details',
            }}
          />
        ),
      },
      {
        id: 'settings-details-editing',
        title: 'Changing one of them',
        tap: 'Tap a fact',
        render: () => (
          <CareProfileScreen
            account={{
              initials: CARER_INITIALS,
              initialSheetOpen: true,
              initialView: 'details',
              // The legal name: it is several fields, so it shows the part of
              // this a text box cannot do. Same list and same editor as the
              // care record, so a name is changed the same way wherever it is
              // changed.
              initialEditingFact: 'account-name',
            }}
          />
        ),
      },
    ]),
  ],
  states: [],
  components: {},
};

/**
 * Care circles, as its own page.
 *
 * The circle is not a setting. Who is in it, what they can see and which of
 * them the app is currently about are the same subject, and that subject is the
 * app's shape rather than a preference inside it: a carer with two parents is
 * running two of these, and everything else in the product sits inside whichever
 * one the bar is naming.
 *
 * Managing one is reached from settings, and switching between them from the
 * name in the bar. Both are here, because a reader asking how circles work
 * should not have to know which control we hung each half on.
 */
export const masterCareCirclesSpec: HandoverSpec = {
  id: 'master-care-circles',
  title: 'Care circles',
  description:
    'Who is in a circle, and which of them the app is about right now.',
  links: [
    {
      label: 'Onboarding: where the first circle is built',
      url: '/onboarding/flows/onboarding',
    },
  ],
  flows: [
    {
      title: 'Managing the circle',
      journey: 'Care circles',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              All of it on the name, none of it in settings. The bar names one
              person and every screen under it is theirs, so the name is where
              anything that changes which circle you are in, or who is in it,
              belongs.
            </span>
            <span>
              It was split for a while, managing in the account menu and
              switching on the name, which left one subject in two places and no
              way to guess which half was where.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'menu',
          title: 'Who you’re caring for',
          tap: 'The name in the bar',
          render: () => (
            <CareProfileScreen
              account={{ initials: CARER_INITIALS, initialSwitcherOpen: true }}
            />
          ),
        },
        {
          id: 'circle',
          title: 'Margaret’s care circle',
          tap: '‘Manage care circle’',
          render: () => (
            <CareProfileScreen
              account={{
                initials: CARER_INITIALS,
                initialSwitcherOpen: true,
                initialCircleView: 'manage',
              }}
            />
          ),
        },
        {
          id: 'circle-invite',
          title: 'Adding somebody',
          tap: '‘Add another’',
          render: () => (
            <CareProfileScreen
              account={{
                initials: CARER_INITIALS,
                initialSwitcherOpen: true,
                initialCircleView: 'manage',
                initialInviteOpen: true,
              }}
            />
          ),
          note: (
            <Note title='Readme' variant='body2'>
              Email and a relationship, and nothing else: the person invited
              puts their own name in when they accept, so asking for it here
              would be asking somebody to guess how a third party spells their
              name.
            </Note>
          ),
        },
      ],
    },
    {
      title: 'Switching circle',
      journey: 'Care circles',
      note: (
        <Note title='Readme' variant='body2'>
          The bar names one person and every screen under it is theirs, so the
          name is what opens the list: anywhere else would be a setting for
          something the header is already saying.
        </Note>
      ),
      steps: [
        {
          id: 'on-a-page',
          title: 'Anywhere in the app',
          tap: 'The name in the bar',
          render: () => (
            <CareProfileScreen account={{ initials: CARER_INITIALS }} />
          ),
        },
        {
          id: 'switcher',
          title: 'Who you’re caring for',
          tap: 'Pick one',
          render: () => (
            <CareProfileScreen
              account={{ initials: CARER_INITIALS, initialSwitcherOpen: true }}
            />
          ),
        },
        {
          id: 'switched',
          title: 'The app, on the other one',
          render: () => (
            <CareProfileScreen
              account={{ initials: CARER_INITIALS, initialCircle: 'Brian' }}
            />
          ),
          note: (
            <Note title='Readme' variant='body2'>
              The header only, in the prototype: there is one set of fixtures,
              so the record under it is still Margaret’s. In the app the switch
              is the whole app — actions, profile, claims, calls.
            </Note>
          ),
        },
      ],
    },
    {
      title: 'Starting another circle',
      journey: 'Care circles',
      steps: [
        {
          id: 'add',
          title: 'Someone else you care for',
          tap: '‘Add someone you’re caring for’',
          render: () => (
            <CareProfileScreen
              account={{
                initials: CARER_INITIALS,
                initialSwitcherOpen: true,
                initialCircleView: 'add',
              }}
            />
          ),
          note: (
            <Note title='Readme' variant='body2'>
              The three things onboarding asks about the first person, because
              starting a circle is that screen again for the second. What
              happens after it is not drawn: presumably the questions, asked
              about them.
            </Note>
          ),
        },
      ],
    },
  ],
  states: [],
  components: {},
};

/**
 * Calls, as its own page.
 *
 * The profile's Calls tab first, then reading what was said on one. The list is
 * the current design and the summaries are the older Call notes page, and they
 * sit together rather than one replacing the other: the tab is where you find a
 * call, and those screens are the only place we have drawn what opening one
 * gives you.
 */
export const masterCallsSpec: HandoverSpec = {
  id: 'master-calls',
  title: 'Calls',
  description: 'Every call on the record, and what was said on one.',
  links: [
    {
      label: 'Call notes: concepting',
      url: '/meaningful-interactions/flows/care-calls',
    },
  ],
  flows: [
    {
      title: 'The calls on the record',
      journey: 'Calls',
      steps: [
        {
          id: 'calls-list',
          title: 'Calls',
          tap: '‘Calls’',
          render: () => (
            <CareProfileScreen
              initialTab='calls'
              account={{ initials: CARER_INITIALS }}
            />
          ),
        },
        {
          id: 'calls-summary',
          title: 'What was said',
          tap: 'Tap a call',
          render: () => (
            <CareProfileScreen
              initialTab='calls'
              initialCall={1}
              account={{ initials: CARER_INITIALS }}
            />
          ),
          note: (
            <Note title='Readme' variant='body2'>
              Pushed over the tab, not opened as a sheet: a summary is a page
              with a back, and it carries the actions the call left behind, so
              it has more in it than a sheet should hold.
            </Note>
          ),
        },
        {
          id: 'calls-action',
          title: 'An action it left behind',
          tap: 'Tap a linked action',
          render: () => (
            <CareProfileScreen
              initialTab='calls'
              initialCall={1}
              initialCallAction={101}
              account={{ initials: CARER_INITIALS }}
            />
          ),
          note: (
            <Note title='Readme' variant='body2'>
              The same sheet the Actions tab opens. An action belongs to the
              call it came out of as much as to the list, so it opens over the
              summary rather than sending you to the tab to find it.
            </Note>
          ),
        },
        {
          id: 'calls-pending',
          title: 'Before there is a summary',
          tap: 'Tap a call that has not happened',
          render: () => (
            <CareProfileScreen
              initialTab='calls'
              initialCall={2}
              account={{ initials: CARER_INITIALS }}
            />
          ),
        },
      ],
    },
  ],
  states: [],
  components: {},
};

/**
 * Docs, as its own page.
 *
 * The profile's Docs tab first, then uploading one and managing it after. The
 * list is the current design and the sheets are the older Key documents page,
 * and they sit together: the tab is where the documents are, and those screens
 * are the only place we have drawn what adding one actually involves.
 */
export const masterDocsSpec: HandoverSpec = {
  id: 'master-docs',
  title: 'Docs',
  description: 'What we have been given, and how another one gets added.',
  links: [
    {
      label: 'Key documents: concepting',
      url: '/meaningful-interactions/flows/care-documents',
    },
  ],
  flows: [
    {
      title: 'What we hold, and adding to it',
      journey: 'Docs',
      steps: [
        {
          id: 'docs-list',
          title: 'Docs',
          tap: '‘Docs’',
          render: () => (
            <CareProfileScreen
              initialTab='docs'
              account={{ initials: CARER_INITIALS }}
            />
          ),
          note: (
            <Note title='Readme' variant='body2'>
              Upload floats under the list rather than sitting at the end of it:
              a doc list has no end worth scrolling to, and it lands in the same
              place as the action on the About tab.
            </Note>
          ),
        },
        {
          id: 'docs-uploading',
          title: 'Adding one',
          tap: '‘Upload document’',
          render: () => (
            <CareProfileScreen
              initialTab='docs'
              uploading
              account={{ initials: CARER_INITIALS }}
            />
          ),
          note: (
            <Note title='Readme' variant='body2'>
              The row lands first and fills in after, so the tap is answered by
              the list rather than by a spinner over it.
            </Note>
          ),
        },
      ],
    },
    {
      title: 'Managing one',
      journey: 'Docs',
      steps: [
        {
          id: 'docs-actions',
          title: 'What you can do to it',
          tap: 'Tap a document',
          render: () => (
            <CareProfileScreen
              initialTab='docs'
              initialDoc='council-tax'
              account={{ initials: CARER_INITIALS }}
            />
          ),
        },
        {
          id: 'docs-rename',
          title: 'Renaming it',
          tap: '‘Edit’',
          render: () => (
            <CareProfileScreen
              initialTab='docs'
              initialRenaming='council-tax'
              account={{ initials: CARER_INITIALS }}
            />
          ),
          note: (
            <Note title='Readme' variant='body2'>
              Delete is in the same sheet and takes the row with it. Both sheets
              are the Care tab's own: a document is the same object wherever it
              is seen, so it should not be managed two different ways.
            </Note>
          ),
        },
      ],
    },
  ],
  states: [],
  components: {},
};

/**
 * Help, as its own page.
 *
 * Being stuck has one tab. It was a tab called Chat with the articles over on
 * Home and webinars nowhere, which asked somebody who is stuck to work out
 * whether their problem was a talking problem or a reading problem before they
 * could go anywhere. All three ways are on one screen now, and talking is
 * first: a person who opens Help usually wants a person.
 */
export const masterHelpSpec: HandoverSpec = {
  id: 'master-help',
  title: 'Help',
  description: 'Talking to somebody, and the things you can read or attend.',
  links: [],
  flows: [
    {
      title: 'Getting unstuck',
      journey: 'Help',
      steps: [
        {
          id: 'help-tab',
          title: 'Help',
          tap: 'The Help tab',
          render: () => (
            <AppTab active='help'>
              <HelpScreen title={RECIPIENT} />
            </AppTab>
          ),
        },
        {
          id: 'help-webinars',
          title: 'Every webinar',
          tap: '‘Webinars’',
          render: () => (
            <AppTab active='help'>
              <HelpScreen title={RECIPIENT} initialList='webinars' />
            </AppTab>
          ),
        },
        {
          id: 'help-articles',
          title: 'Every article',
          tap: '‘Help articles’',
          render: () => (
            <AppTab active='help'>
              <HelpScreen title={RECIPIENT} initialList='articles' />
            </AppTab>
          ),
        },
      ],
    },
  ],
  states: [],
  components: {},
};

/**
 * The benefits calculator, as its own page.
 *
 * It starts on the Profile's Assess tab and runs somewhere else entirely, which
 * is why Profile links it rather than drawing it: a claim is its own journey,
 * and hanging it off the tab it happens to begin on made Profile a page about
 * two things.
 */
export const masterBenefitsCalculatorSpec: HandoverSpec = {
  id: 'master-benefits-calculator',
  title: 'Benefits calculator',
  description: 'Working out what they could claim, from what we already hold.',
  links: [
    {
      label: 'Benefits calculator: concepting',
      url: '/tell-us-once/flows/benefits-calculator',
    },
  ],
  flows: under('Benefits calculator', benefitsCalculatorFlowsSpec.flows),
  states: [],
  components: {},
};

/**
 * Attendance Allowance, as its own page.
 *
 * The claim the calculator sends you to, and the longest form in the app: 227
 * questions across fourteen sections, most of which we can answer from what the
 * intake already read.
 */
export const masterAttendanceAllowanceSpec: HandoverSpec = {
  id: 'master-attendance-allowance',
  title: 'Attendance Allowance',
  description: 'The claim itself, and how much of it we can fill in for them.',
  links: [
    {
      label: 'Attendance allowance: concepting',
      url: '/tell-us-once/flows/attendance-allowance',
    },
  ],
  flows: under('Attendance Allowance', attendanceAllowanceFlowsSpec.flows),
  states: [],
  components: {},
};

/**
 * The care record, tab by tab.
 *
 * Profile, not Care: Care is the tab where care is arranged, and this is the
 * record of the person it is being arranged for.
 *
 * Drawn from CareProfileScreen, which is the profile as it stands — four tabs,
 * the recipient named in the bar, the facts editable in place. The older Calls
 * and Docs pages describe the tabs this screen replaced, so composing those
 * here put the previous design on a page about the current one.
 *
 * The calculator is linked rather than drawn. It starts on the Assess tab, so
 * the tab is on this page and the journey it opens is on its own.
 */
const profileTab = (tab: (typeof PROFILE_TABS)[number]) => ({
  id: `profile-${tab.id}`,
  title: tab.label,
  tap: `‘${tab.label}’`,
  render: () => (
    <CareProfileScreen
      initialTab={tab.id}
      account={{ initials: CARER_INITIALS }}
    />
  ),
});

export const masterProfileSpec: HandoverSpec = {
  id: 'master-profile',
  title: 'Profile',
  description: 'The care record: what we hold, and what gets assessed.',
  links: [
    {
      label: 'Tell Us Once: the profile in the flow',
      url: '/tell-us-once/flows/intake-profile-review',
    },
    {
      label: 'Benefits calculator: concepting',
      url: '/tell-us-once/flows/benefits-calculator',
    },
  ],
  flows: [
    {
      title: 'The four tabs',
      journey: 'Profile',
      steps: PROFILE_TABS.map(profileTab),
    },
    {
      title: 'Changing something we hold',
      journey: 'Profile',
      steps: [
        {
          id: 'about',
          title: 'What we hold',
          tap: 'Tap a fact',
          render: () => (
            <CareProfileScreen
              initialTab='profile'
              account={{ initials: CARER_INITIALS }}
            />
          ),
        },
        {
          id: 'about-editing',
          title: 'Opened, as the thing it is',
          tap: '‘Done’',
          // The address rather than a name or a phone number: it is four
          // fields, so it shows the part of this that a text box cannot do.
          render: () => (
            <CareProfileScreen
              initialTab='profile'
              editing='address'
              account={{ initials: CARER_INITIALS }}
            />
          ),
        },
      ],
    },
  ],
  states: [],
  components: {},
};
